import { Tooltip, TooltipContent, TooltipTrigger } from '@design-system';
import { HeaderTooltip } from '../HeaderTooltip';
import { traditionalLtcPresentation } from './traditionalLtcPresentation';
import type { StrategyPresentation } from './types';

// CCAH membership programs (Fortified Life, Stay Your Way) — single-pool
// reimbursement like traditional LTC, but with NO waiting period and NO 2-ADL
// trigger (all care is benefit-eligible from the membership date), and real
// contracts define no daily benefit cap. Wraps the traditional-LTC
// presentation and overrides only the surfaces where the inherited
// traditional-LTC framing is wrong for a membership (P2-27, P2-28).

export const membershipProgramPresentation: StrategyPresentation = {
  ...traditionalLtcPresentation,

  // Uncapped programs (`terms.hasConfiguredBenefitCap === false`) have no
  // policy benefit cap — the engine sets each row's effective cap to its
  // eligible cost, so echoing `appliedMonthlyBenefitCap` would display the
  // month's COST as if it were a dynamic policy cap (P2-28). Render
  // "Uncapped" instead; capped programs keep the traditional cell.
  benefitAmountCell: ctx => {
    if (ctx.terms.hasConfiguredBenefitCap !== false) {
      return traditionalLtcPresentation.benefitAmountCell!(ctx);
    }
    if (ctx.month.events.ineligibilityReason === 'pre-policy') return null;
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="cursor-help leading-tight">
            <div className="font-medium text-gray-900">Uncapped</div>
          </div>
        </TooltipTrigger>
        <TooltipContent className="max-w-xs text-xs">
          This program has no per-month benefit cap. Payout is limited by the eligible care cost
          and the program pool / care duration, not a policy benefit amount.
        </TooltipContent>
      </Tooltip>
    );
  },

  // The uncapped cell renders a constant label, so there is no per-row value
  // to compare for the dim-unchanged keying.
  benefitAmountValueForMonth: ctx =>
    ctx.terms.hasConfiguredBenefitCap === false
      ? null
      : traditionalLtcPresentation.benefitAmountValueForMonth!(ctx),

  copy: {
    ...traditionalLtcPresentation.copy,
    // Membership programs have no elimination period and no 2-ADL trigger —
    // the inherited traditional-LTC note would tell members their eligibility
    // is gated by an EP/ADL trigger the product doesn't have (P2-27).
    payoutColumnTooltip: (
      <HeaderTooltip
        description="Policy payout this month, with cumulative dollars paid by the policy through this month in parentheses."
        formulas={[
          {
            label: 'Reimbursement mode',
            expression: 'min(eligible cost, monthly maximum, pool remaining)',
          },
          { label: 'Indemnity mode', expression: 'min(monthly maximum, pool remaining)' },
        ]}
        notes="All care is benefit-eligible from the membership date — there is no waiting period or 2-ADL gate."
      />
    ),
  },
};
